import { useState, useEffect, useRef } from 'react';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getProjects, deleteProject, updateProject, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LoadError } from '../components/LoadError';
import { TruncationNotice, totalFromHeaders } from '../components/TruncationNotice';
import { 
    Plus, 
    Search, 
    Film, 
    Star,
    StarOff,
    Archive,
    ArchiveRestore,
    MoreVertical,
    Edit,
    Trash2,
    HardDrive,
    LayoutGrid,
    List
} from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [total, setTotal] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('ad_master_projects_view') || 'grid');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [deleteId, setDeleteId] = useState(null);
    const { isManager } = useAuth();
    const navigate = useNavigate();
    const debouncedSearch = useDebouncedValue(search);
    // Only the latest request may update the list (typing fast must not show stale results)
    const requestId = useRef(0);

    useEffect(() => {
        fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [filter, debouncedSearch]);

    const fetchProjects = async () => {
        const id = ++requestId.current;
        try {
            setLoadError(null);
            const params = {};
            if (filter === 'favorites') params.favorite = true;
            if (filter === 'archived') params.archived = true;
            if (filter === 'active') params.archived = false;
            if (debouncedSearch) params.search = debouncedSearch;

            const response = await getProjects(params);
            if (id !== requestId.current) return;
            const sorted = [...response.data].sort((a, b) => 
                (a.title || '').localeCompare(b.title || '', 'fr', { sensitivity: 'base' })
            );
            setProjects(sorted);
            setTotal(totalFromHeaders(response));
        } catch (error) {
            if (id !== requestId.current) return;
            if (!error._isAuthError) setLoadError(errorMessage(error, 'Erreur lors du chargement des projets'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    };

    const handleToggleFavorite = async (project) => {
        try {
            await updateProject(project.id, { favorite: !project.favorite });
            toast.success(project.favorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
            fetchProjects();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la mise à jour'));
        }
    };

    const handleToggleArchive = async (project) => {
        try {
            await updateProject(project.id, { archived: !project.archived });
            toast.success(project.archived ? 'Projet désarchivé' : 'Projet archivé');
            fetchProjects();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la mise à jour'));
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteProject(deleteId);
            toast.success('Projet supprimé');
            setDeleteId(null);
            fetchProjects();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const toggleView = (mode) => {
        setViewMode(mode);
        localStorage.setItem('ad_master_projects_view', mode);
    };

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="projects-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            PROJETS
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Gérez vos films et productions
                        </p>
                    </div>
                    {isManager() && (
                        <Button asChild data-testid="new-project-button">
                            <Link to="/projects/new">
                                <Plus className="w-4 h-4 mr-2" />
                                Nouveau projet
                            </Link>
                        </Button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Rechercher un projet..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="project-search-input"
                        />
                    </div>
                    <Tabs value={filter} onValueChange={setFilter}>
                        <TabsList>
                            <TabsTrigger value="all" data-testid="filter-all">Tous</TabsTrigger>
                            <TabsTrigger value="active" data-testid="filter-active">Actifs</TabsTrigger>
                            <TabsTrigger value="favorites" data-testid="filter-favorites">Favoris</TabsTrigger>
                            <TabsTrigger value="archived" data-testid="filter-archived">Archivés</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <div className="flex gap-1 ml-auto">
                        <Button
                            variant={viewMode === 'grid' ? 'default' : 'outline'}
                            size="icon" aria-label="Vue grille" aria-pressed={viewMode === 'grid'}
                            className="h-9 w-9"
                            onClick={() => toggleView('grid')}
                            data-testid="view-grid"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'default' : 'outline'}
                            size="icon" aria-label="Vue liste" aria-pressed={viewMode === 'list'}
                            className="h-9 w-9"
                            onClick={() => toggleView('list')}
                            data-testid="view-list"
                        >
                            <List className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Projects Grid */}
                {!loading && !loadError && <TruncationNotice shown={projects.length} total={total} noun="projets" />}
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                        {[...Array(12)].map((_, i) => (
                            <Skeleton key={i} className="h-48" />
                        ))}
                    </div>
                ) : loadError ? (
                    <LoadError message={loadError} onRetry={() => { setLoading(true); fetchProjects(); }} />
                ) : projects.length === 0 ? (
                    <Card className="bg-card border-border">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <Film className="w-16 h-16 text-muted-foreground/50 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">
                                Aucun projet
                            </h3>
                            <p className="text-muted-foreground text-center max-w-md mb-6">
                                {search ? 'Aucun projet ne correspond à votre recherche.' : 'Commencez par créer votre premier projet.'}
                            </p>
                            {isManager() && !search && (
                                <Button asChild>
                                    <Link to="/projects/new">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Créer un projet
                                    </Link>
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <>
                    {/* Grid View */}
                    {viewMode === 'grid' && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                        {projects.map((project) => (
                            <Card 
                                key={project.id} 
                                className="bg-card border-border hover:border-primary/30 transition-colors overflow-hidden group"
                                data-testid={`project-card-${project.id}`}
                            >
                                <Link to={`/projects/${project.id}`}>
                                    <div className="aspect-[4/5] relative overflow-hidden bg-secondary">
                                        {project.poster_thumb ? (
                                            <img
                                                src={project.poster_thumb}
                                                alt={project.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Film className="w-10 h-10 text-muted-foreground/30" />
                                            </div>
                                        )}
                                        {project.archived && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                <Badge variant="secondary" className="text-xs">
                                                    <Archive className="w-3 h-3 mr-1" />
                                                    Archivé
                                                </Badge>
                                            </div>
                                        )}
                                        {project.favorite && (
                                            <div className="absolute top-1.5 left-1.5">
                                                <Star className="w-4 h-4 text-primary fill-primary" />
                                            </div>
                                        )}
                                    </div>
                                </Link>
                                <CardContent className="p-2.5">
                                    <div className="flex items-start justify-between gap-1">
                                        <div className="flex-1 min-w-0">
                                            <Link 
                                                to={`/projects/${project.id}`}
                                                className="font-heading font-semibold text-sm hover:text-primary transition-colors line-clamp-1"
                                            >
                                                {project.title}
                                            </Link>
                                            <p className="text-xs text-muted-foreground">
                                                {project.production_year || 'N/A'}
                                            </p>
                                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                                <HardDrive className="w-3 h-3" />
                                                {project.masters_count}
                                            </div>
                                        </div>
                                        {isManager() && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" aria-label={`Actions pour ${project.title}`} className="h-6 w-6" data-testid={`project-menu-${project.id}`}>
                                                        <MoreVertical className="w-3.5 h-3.5" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}/edit`)}>
                                                        <Edit className="w-4 h-4 mr-2" />
                                                        Modifier
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleToggleFavorite(project)}>
                                                        {project.favorite ? (
                                                            <>
                                                                <StarOff className="w-4 h-4 mr-2" />
                                                                Retirer des favoris
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Star className="w-4 h-4 mr-2" />
                                                                Ajouter aux favoris
                                                            </>
                                                        )}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleToggleArchive(project)}>
                                                        {project.archived ? (
                                                            <>
                                                                <ArchiveRestore className="w-4 h-4 mr-2" />
                                                                Désarchiver
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Archive className="w-4 h-4 mr-2" />
                                                                Archiver
                                                            </>
                                                        )}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem 
                                                        onClick={() => setDeleteId(project.id)}
                                                        className="text-destructive-text focus:text-destructive-text"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Supprimer
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    )}

                    {/* List View */}
                    {viewMode === 'list' && (
                    <Card className="bg-card border-border">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                    <TableHead className="w-[50px]"></TableHead>
                                    <TableHead className="font-bold text-xs uppercase tracking-wider">Titre</TableHead>
                                    <TableHead className="font-bold text-xs uppercase tracking-wider">Année</TableHead>
                                    <TableHead className="font-bold text-xs uppercase tracking-wider">Masters</TableHead>
                                    <TableHead className="font-bold text-xs uppercase tracking-wider w-[80px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {projects.map((project) => (
                                    <TableRow 
                                        key={project.id} 
                                        className="hover:bg-muted/30 cursor-pointer"
                                        onClick={() => navigate(`/projects/${project.id}`)}
                                        data-testid={`project-row-${project.id}`}
                                    >
                                        <TableCell className="p-2">
                                            <div className="w-8 h-10 rounded overflow-hidden bg-secondary flex-shrink-0">
                                                {project.poster_thumb ? (
                                                    <img src={project.poster_thumb} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Film className="w-4 h-4 text-muted-foreground/30" />
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {project.favorite && <Star className="w-3.5 h-3.5 text-primary fill-primary flex-shrink-0" />}
                                                <Link
                                                    to={`/projects/${project.id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="font-medium text-sm hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                >
                                                    {project.title}
                                                </Link>
                                                {project.archived && (
                                                    <Badge variant="secondary" className="text-xs">Archivé</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{project.production_year || 'N/A'}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                <HardDrive className="w-3 h-3" />
                                                {project.masters_count}
                                            </div>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {isManager() && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" aria-label={`Actions pour ${project.title}`} className="h-7 w-7">
                                                            <MoreVertical className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}/edit`)}>
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Modifier
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleToggleFavorite(project)}>
                                                            {project.favorite ? <><StarOff className="w-4 h-4 mr-2" />Retirer des favoris</> : <><Star className="w-4 h-4 mr-2" />Ajouter aux favoris</>}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => handleToggleArchive(project)}>
                                                            {project.archived ? <><ArchiveRestore className="w-4 h-4 mr-2" />Désarchiver</> : <><Archive className="w-4 h-4 mr-2" />Archiver</>}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => setDeleteId(project.id)} className="text-destructive-text focus:text-destructive-text">
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Supprimer
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                    )}
                    </>
                )}
            </div>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Tous les masters et mouvements associés seront également supprimés.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
